import { incrementMark } from "@local/extensions/identifiers/mark.js";

// Where a literal match of `find` sits in `mark`, honouring `position`.
// Returns the indices of every match to replace, in ascending order. Literal
// string scanning rather than a RegExp, so marks containing ( . - [ ] etc.
// are matched as typed instead of being read as a pattern.
function matchIndices(mark, find, position, matchCase) {
  const haystack = matchCase ? mark : mark.toLowerCase();
  const needle = matchCase ? find : find.toLowerCase();

  if (position === "start") {
    return haystack.startsWith(needle) ? [0] : [];
  }

  if (position === "end") {
    return haystack.endsWith(needle) ? [haystack.length - needle.length] : [];
  }

  const indices = [];
  let from = 0;
  for (;;) {
    const at = haystack.indexOf(needle, from);
    if (at === -1) break;
    indices.push(at);
    // Step past this match so overlapping needles can't match twice.
    from = at + needle.length;
  }
  return indices;
}

function replaceAt(mark, indices, findLength, replace) {
  let out = "";
  let cursor = 0;
  indices.forEach((at) => {
    out += mark.slice(cursor, at) + replace;
    cursor = at + findLength;
  });
  return out + mark.slice(cursor);
}

function findReplaceMark(mark, { find, replace, position, matchCase }) {
  // An empty needle matches between every character, which would splice the
  // replacement throughout the mark. Treat it as a no-op instead.
  if (!find) return mark;

  const indices = matchIndices(mark, find, position, matchCase);
  if (!indices.length) return mark;

  const next = replaceAt(mark, indices, find.length, replace);

  // Removing text usually leaves a stranded separator at one end. Only the
  // outer edges are trimmed; whitespace inside the mark is left alone.
  const trimmed = next.trim();

  // Never trim a mark down to nothing.
  return trimmed || mark;
}

function renumberMarks(count, { start, prefix, suffix }) {
  const marks = [];
  let current = start;
  for (let i = 0; i < count; i += 1) {
    marks.push(`${prefix}${current}${suffix}`);
    current = incrementMark(current);
  }
  return marks;
}

function addTextMark(mark, { prefix, suffix }) {
  // A frame with no mark would end up with one made only of prefix and
  // suffix, which is not a mark anybody asked for.
  if (!mark) return mark;
  return `${prefix}${mark}${suffix}`;
}

/**
 * Compute replacement marks for a list of existing marks.
 *
 * `marks` arrives in the order the new marks should be applied — callers do
 * their own sorting, reversing and de-duplication first. The returned array is
 * always the same length as `marks`, and entries that the options leave
 * untouched come back unchanged.
 *
 * @param {string[]} marks existing marks, in apply order
 * @param {object} options
 * @param {"renumber"|"addText"|"findReplace"} options.mode
 * @param {string} [options.start] renumber: first mark, then incremented
 * @param {string} [options.prefix] renumber, addText: constant leading text
 * @param {string} [options.suffix] renumber, addText: constant trailing text
 * @param {string} [options.find] findReplace: literal text to match
 * @param {string} [options.replace] findReplace: literal replacement, "" removes
 * @param {"start"|"end"|"anywhere"} [options.position] findReplace: where to match
 * @param {boolean} [options.matchCase] findReplace: case-sensitive when true
 * @returns {string[]}
 */
export function applyMarkUpdate(marks, options = {}) {
  const list = marks || [];
  const {
    mode = "renumber",
    start = "",
    prefix = "",
    suffix = "",
    find = "",
    replace = "",
    position = "anywhere",
    matchCase = false,
  } = options;

  if (mode === "renumber") {
    if (!start) return list.slice();
    return renumberMarks(list.length, { start, prefix, suffix });
  }

  if (mode === "addText") {
    if (!prefix && !suffix) return list.slice();
    return list.map((mark) => addTextMark(mark, { prefix, suffix }));
  }

  if (mode === "findReplace") {
    return list.map((mark) =>
      findReplaceMark(mark || "", { find, replace, position, matchCase }),
    );
  }

  return list.slice();
}

// Two marks are the same mark to anyone reading a drawing, whatever the
// casing — A11 and a11 are a collision, not two marks.
function collisionKey(mark) {
  return mark.toLowerCase();
}

function countByKey(marks) {
  const counts = new Map();
  (marks || []).forEach((mark) => {
    if (!mark) return;
    const key = collisionKey(mark);
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return counts;
}

/**
 * Marks this update would newly put on more than one record.
 *
 * Deliberately not "every mark shared by two records afterwards". A mark that
 * was already on two records and is still on two records is a pre-existing
 * condition, not something this operation did, and reporting it trains people
 * to ignore the warning. So the count of records carrying a mark has to RISE,
 * and end up above one, before it is reported.
 *
 * Comparison ignores case throughout: A11 and a11 are one mark.
 *
 * @param {object} args
 * @param {string[]} args.oldMarks current marks of the records being updated
 * @param {string[]} args.newMarks proposed marks, positionally matched to oldMarks
 * @param {string[]} [args.otherMarks] marks of records in the same namespace
 *   that this update does not touch
 * @returns {string[]} newly-colliding marks, in first-seen order
 */
export function createdDuplicates({ oldMarks, newMarks, otherMarks = [] }) {
  const before = countByKey([...(oldMarks || []), ...otherMarks]);
  const after = countByKey([...(newMarks || []), ...otherMarks]);

  const reported = new Set();
  const clashes = [];

  (newMarks || []).forEach((mark) => {
    if (!mark) return;
    const key = collisionKey(mark);
    if (reported.has(key)) return;

    const now = after.get(key) || 0;
    const was = before.get(key) || 0;
    if (now > 1 && now > was) {
      reported.add(key);
      clashes.push(mark);
    }
  });

  return clashes;
}

/**
 * Whether `mark` is one of the collisions `createdDuplicates` reported. Needed
 * because the reported list carries one spelling and a row may hold another.
 *
 * @param {string[]} clashes result of createdDuplicates
 * @param {string} mark
 * @returns {boolean}
 */
export function isDuplicateMark(clashes, mark) {
  if (!mark) return false;
  const key = collisionKey(mark);
  return (clashes || []).some((clash) => collisionKey(clash) === key);
}
